import { Stack, styled } from '@mui/material'
import { tokens } from 'ComponentLibrary'
import { Typography } from '../../text'
import { ReactElement } from 'react'
import { Check, CheckCircle, Circle, CircleDashed, CircleHalf, IconContext, Warning } from '@phosphor-icons/react'

export type InfoStatusVariant = 'positive' | 'ordinary' | 'warning' | 'negative' | 'neutral'

type InfoStatusProps = {
    variant: InfoStatusVariant
    size: 'small' | 'medium' | 'large'
    label?: string
}

export function InfoStatus({ variant, size, label }: InfoStatusProps) {
    return (
        <Stack direction="row" gap={tokens.SizingSpacing075} alignItems="center">
            <IconContext.Provider
                value={{
                    size: tokens.SizingIconS,
                    weight: size === 'large' ? 'bold' : 'fill',
                    color: size === 'large' ? tokens.ColorIconInverse : getInfoStatusIconProps(size)[variant].color,
                }}
            >
                {size === 'large' ? (
                    <IconContainer $variant={variant}>{getInfoStatusIconProps(size)[variant].icon}</IconContainer>
                ) : (
                    getInfoStatusIconProps(size)[variant].icon
                )}
            </IconContext.Provider>

            {label && (
                <Typography variant={size === 'small' ? 'textS' : 'textM'} color={tokens.ColorTextBold} noWrap>
                    {label}
                </Typography>
            )}
        </Stack>
    )
}

const IconContainer = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $variant: InfoStatusProps['variant'] }>(({ $variant }) => ({
    width: tokens.SizingIconM,
    height: tokens.SizingIconM,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: getInfoStatusIconProps('large')[$variant].backgroundColor,
    borderRadius: tokens.SizingCornerS,
}))

const getInfoStatusIconProps = (
    size: InfoStatusProps['size']
): Record<InfoStatusProps['variant'], { icon: ReactElement; color: string; backgroundColor: string }> => ({
    positive: {
        icon: size === 'large' ? <Check /> : <CheckCircle />,
        color: tokens.ColorIconPositive,
        backgroundColor: tokens.ColorSurfacePositiveBoldDefault,
    },
    ordinary: {
        icon: <Circle />,
        color: tokens.ColorIconInformative,
        backgroundColor: tokens.ColorSurfaceInformativeBoldDefault,
    },
    warning: {
        icon: <CircleHalf />,
        color: tokens.ColorIconWarning,
        backgroundColor: tokens.ColorSurfaceWarningBoldDefault,
    },
    negative: {
        icon: <Warning />,
        color: tokens.ColorIconDanger,
        backgroundColor: tokens.ColorSurfaceDangerBoldDefault,
    },
    neutral: {
        icon: <CircleDashed />,
        color: tokens.ColorIconAccentGrey,
        backgroundColor: tokens.ColorSurfaceAccentGreyBold,
    },
})
